import { Component, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';

import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, withLatestFrom } from 'rxjs/operators';

import { AudioVideoConnectionMonitorSelectors } from 'src/app/features/audio-video-connection-monitor/store';
import { ClientSelectors } from 'src/app/features/client';
import { ClientTheme } from 'src/app/features/client/models';
import { AudioVideoModalContainerComponent } from 'src/app/features/layout/components/audio-video-modal/audio-video-modal-container.component';
import { ModalsActions, ModalSize } from 'src/app/features/modals';
import { MsalAuthSelectors } from 'src/app/features/msal-auth';
import { PackageUsersSelectors } from 'src/app/features/package-users';
import { PackagesSelectors, ProductType } from 'src/app/features/packages';
import { DEFAULT_APPLICATION_PHONE_NUMBER } from 'src/app/features/shared/constants';
import { SigningRoomSelectors } from 'src/app/features/signing-room/store/selectors';
import { RootStoreState } from 'src/app/store';
import {
  IdVerifyComponent,
  SigningDetailsComponent,
} from '../components';
import { SigningRoomActions } from '../../signing-room/store';
import { FeatureManagementService } from '../../feature-management/services';
import { Feature } from '../../feature-management/models';
import { DeviceGroupSelectors } from '../../device-group';

@Component({
    selector: 'app-nav-bar-signing-session',
    templateUrl: './nav-bar-signing-session.component.html',
    styleUrls: ['../nav-bar/nav-bar.component.scss'],
    standalone: false
})
export class NavBarSigningSessionComponent implements OnInit {
  canViewOrderDetailsButton$: Observable<boolean>;
  canViewDeviceSettingsButton$: Observable<boolean>;
  canViewIdVerifyButton$: Observable<boolean>;
  canViewAddUserButton$: Observable<boolean>;
  clientTheme$: Observable<ClientTheme>;
  onDestroyNotifier = new Subject();

  clearSignSupportNumber = DEFAULT_APPLICATION_PHONE_NUMBER;
  isAddUserToSessionEnabled: boolean;

  constructor( private readonly store: Store<RootStoreState.State>, private readonly featureManagementService: FeatureManagementService) {   }

  ngOnInit(): void {
    this.canViewOrderDetailsButton$ = combineLatest([
      this.store.pipe(select(PackageUsersSelectors.getIsActivePackageUserSigningAgent)),
      this.store.pipe(select(MsalAuthSelectors.getIsUserVerified)),
    ]).pipe(map(([isSigningAgent, msalIsVerified]) => isSigningAgent || msalIsVerified));

    this.canViewDeviceSettingsButton$ = this.store.pipe(
      select(AudioVideoConnectionMonitorSelectors.getCanDeviceSettingsModalBeDisplayed)
    );

    this.clientTheme$ = this.store.pipe(select(ClientSelectors.getClientTheme));

    this.canViewIdVerifyButton$ = combineLatest([
      this.store.pipe(select(PackagesSelectors.getProductType)),
      this.store.pipe(select(SigningRoomSelectors.sessionJoined)),
      this.store.pipe(select(PackageUsersSelectors.getIsActivePackageUserSigningAgent)),
    ]).pipe(
      map(([product, inSession, isUserSigningAgent]) => {
        if (product !== ProductType.RemoteSigning) {
          return false;
        } else {
          return (inSession && isUserSigningAgent);
        }
      })
    );
    this.isAddUserToSessionEnabled = this.featureManagementService.getIsFeatureEnabledWithCaching(Feature.AddUserToSession) ;

    
    this.canViewAddUserButton$ = combineLatest([
      this.store.pipe(select(PackagesSelectors.getProductType)),
      this.store.pipe(select(SigningRoomSelectors.sessionJoined)),
      this.store.pipe(select(PackageUsersSelectors.getIsActivePackageUserSigningAgent)),
      this.store.pipe(select(PackageUsersSelectors.getNonSigningAgents), map((nonSas) => nonSas?.length), distinctUntilChanged()),
      this.store.pipe(select(DeviceGroupSelectors.getUsersOnDevice), map((deviceUsers) => deviceUsers?.length), distinctUntilChanged()),
    ]).pipe(
      map(([product, inSession, isUserSigningAgent, nonSaCount, usersOnDeviceCount]) => {
        if (product !== ProductType.RemoteSigning) {
          return false;
        } else {
          return (
            inSession &&
            !isUserSigningAgent &&
            nonSaCount > 1 &&
            this.isAddUserToSessionEnabled &&
            usersOnDeviceCount !== nonSaCount
          );
        }
      })
    );

    this.clientTheme$ = this.store.pipe(select(ClientSelectors.getClientTheme));

  }

  openDetailsModal() {
    this.store.dispatch(
      ModalsActions.SetStandaloneModalComponent({
        payload: {
          component: SigningDetailsComponent,
        },
      })
    );
  }

  openChangeAudioVideoModal() {
    this.store.dispatch(
      ModalsActions.SetStandaloneModalComponent({
        payload: {
          component: AudioVideoModalContainerComponent,
          modalSize: ModalSize.large,
        },
      })
    );
  }

  openCancelSigningModal() {
    this.store.dispatch(ModalsActions.OpenCancelSigningModal());
  }

  openIdVerifyModal() {
    this.store.dispatch(
      ModalsActions.SetStandaloneModalComponent({
        payload: {
          component: IdVerifyComponent,
          modalSize: ModalSize.large,
        },
      })
    );
  }

  openAddUserToThisSession() {
    this.store.dispatch(SigningRoomActions.OpenAddUserToSessionModal());
  }


}
